import { Form, useFormikContext } from 'formik';
import React, { Fragment, useContext } from 'react';
import { WeChatTutorial } from 'Components/widgets/payment-info/payment-process/WeChatTutorial';
import { WECHAT_PSP_ID } from 'Components/widgets/payment-info/PaymentInfo.const';
import { PaymentInfoContext } from 'Components/widgets/payment-info/PaymentInfoContext';
import { PaymentProcess } from 'Components/widgets/payment-info/PaymentProcess';
import { BillingAddressForm } from 'Components/widgets/billing-address-form/BillingAddressForm';
import { Donation } from 'Components/widgets/donation/Donation';
import { FreeDeposit } from './payment-form/FreeDeposit';
import { PaymentPlan } from './payment-form/PaymentPlan';
import { B2BPaymentProvidersForm } from './b2b-payment-info/B2BPaymentProvidersForm';
import { HealthRegulations } from './payment-form/HealthRegulations';
import { TermsAndConditions } from './payment-form/TermsAndConditions';
import { BuyNowPayLater } from './buy-now-pay-later/BuyNowPayLater';
import { PaymentMethods } from './payment-form/PaymentMethods';
import { PaymentProvidersForm } from './payment-form/PaymentProvidersForm';
import { PaymentProviderForm } from './payment-form/PaymentProviderForm';
import { usePaymentForm } from './payment-form/hooks/usePaymentForm';
import { B2BFreeDeposit } from './b2b-payment-info/B2BFreeDeposit';
import { PaymentMethodsWithConditions } from './payment-method/PaymentMethodsWithConditions';
type Props = {
  i18n: I18N;
  shouldShowPaymentProcess: boolean;
  paymentProcessId: number | null | undefined;
  paymentProcessOnError: boolean;
  paymentSchedule: PaymentSchedule;
  paymentProviders: ReadonlyArray<PaymentProvider>;
  buyNowPayLaterProviders: ReadonlyArray<PaymentProvider>;
  price: Price;
  totalPrice: Price | null | undefined;
  deadlineDate: number | null | undefined;
  dispatch: (arg0: any) => void;
};
export const PaymentForm = ({
  i18n,
  shouldShowPaymentProcess,
  paymentProcessId,
  paymentProcessOnError,
  paymentProviders,
  buyNowPayLaterProviders,
  paymentSchedule,
  price,
  totalPrice,
  deadlineDate,
  dispatch,
}: Props) => {
  const formik = useFormikContext();
  const { currency } = useContext(PaymentInfoContext);
  const {
    hasFullAmount,
    currentPaymentPlans,
    isPaymentPlansLoading,
    showPaymentMethodWithConditions,
    shouldAutoSubmitForm,
    hasPaymentProvidersMatchingBlock,
    displayNewPaymentProvidersImplementation,
    selectedPaymentProvider,
    disableFields,
    hasNoPaymentMethods,
    healthRegulations,
    shouldDisplayDonationTerms,
    shouldDisplayBNPLIframe,
    setLoadedPlan,
    setHasFullAmount,
    paymentInProgress,
    paymentPlans,
    totalAmountToPay,
    paymentConditionId,
    paymentMethod,
    paymentFormDisplay,
  } = usePaymentForm({
    i18n,
    paymentSchedule,
    buyNowPayLaterProviders,
    paymentProviders,
    totalPrice: price.value,
    shouldShowPaymentProcess,
    paymentProcessOnError,
  });
  const freeDepositProps = {
    paymentSchedule,
    deadlineDate: deadlineDate || -Infinity,
    price,
    touched: formik.touched?.freeDeposit,
  };
  const paymentFormComponents = [
    {
      component: FreeDeposit,
      name: 'FreeDeposit',
      props: freeDepositProps,
    },
    {
      component: B2BFreeDeposit,
      name: 'B2BFreeDeposit',
      props: { ...freeDepositProps, totalPrice },
    },
    {
      component: PaymentPlan,
      name: 'B2BPaymentPlan',
      props: {
        setHasFullAmount,
        paymentSchedule,
        displayTooltip: false,
        isDisabled: paymentInProgress,
        hasFullAmount,
        payFullAmountLabel: 'booking.partners.banking.paymentSchedule.payFullAmount',
        depositSelectedLabel: 'booking.partners.banking.paymentSchedule.depositSelected',
      },
    },
    {
      component: PaymentPlan,
      name: 'PaymentPlan',
      props: {
        paymentPlans: currentPaymentPlans,
        isLoading: isPaymentPlansLoading,
        setHasFullAmount,
        paymentSchedule,
        hasFullAmount,
      },
    },
    {
      component: PaymentProvidersForm,
      name: 'PaymentProvidersForm',
      props: {
        hasFullAmount,
        paymentProviders,
        paymentSchedule,
        shouldAutoSubmitForm,
        showPaymentMethodWithConditions,
        displayNewPaymentProvidersImplementation,
      },
    },
    {
      component: B2BPaymentProvidersForm,
      name: 'B2BPaymentProvidersForm',
      props: {
        selectedPaymentProvider,
        hasFullAmount,
        isDisabled: paymentInProgress,
        paymentProviders,
        paymentSchedule,
        showPaymentMethodWithConditions,
        shouldAutoSubmitForm,
        hasPaymentProvidersMatchingBlock,
        displayNewPaymentProvidersImplementation,
      },
    },
    {
      component: PaymentMethodsWithConditions,
      name: 'PaymentMethodsWithConditions',
      props: {
        paymentProvider: selectedPaymentProvider,
      },
    },
    {
      component: Donation,
      name: 'Donation',
      testId: 'donation-block',
      props: {
        currency,
        isDisabled: disableFields,
      },
    },
    {
      component: HealthRegulations,
      name: 'HealthRegulations',
      testId: 'contract-info-block',
      props: {
        isDisabled: disableFields,
        shouldSubmitFormOnAccept: false,
        hasNoPaymentMethods,
        healthRegulations,
      },
    },
    {
      component: TermsAndConditions,
      name: 'TermsAndConditions',
      testId: 'cgv-block',
      props: {
        i18n,
        shouldDisplayDonationTerms,
        isDisabled: disableFields,
        shouldSubmitFormOnAccept: shouldAutoSubmitForm,
        hasNoPaymentMethods,
      },
    },
    {
      component: BuyNowPayLater,
      name: 'BuyNowPayLater',
      props: {
        onLoaded: setLoadedPlan,
        price: price.value,
        displayIframe: shouldDisplayBNPLIframe,
        paymentPlans,
      },
    },
    {
      component: PaymentMethods,
      name: 'PaymentMethods',
      props: {
        paymentProvider: selectedPaymentProvider,
        showPaymentMethodWithConditions,
        shouldSubmitFormOnPaymentChosen: shouldAutoSubmitForm,
      },
    },
    {
      component: BillingAddressForm,
      name: 'BillingAddressForm',
      props: {
        i18n,
        path: 'billingDetails',
      },
    },
    {
      component: PaymentProviderForm,
      name: 'PaymentProviderForm',
      props: {
        hasNoPaymentMethods,
        selectedPaymentProvider,
      },
    },
  ].filter(({ name }) => paymentFormDisplay[`display${name}`]);
  return (
    <Fragment>
      <Form className="flex flex-col">
        <input
          name="currentPayingCustomerId"
          type="hidden"
          value={formik.values.currentPayingCustomerId}
        />
        {paymentFormComponents.map(({ component: Component, testId = '', props }, index) => {
          return (
            <div className={`${index && 'mt-20'}`} key={Component.name} data-testId={testId}>
              {/* @ts-expect-error TODO(flow-migration): bind component with props type */}
              <Component {...props} />
            </div>
          );
        })}
      </Form>
      {shouldShowPaymentProcess ? (
        <div className="mt-20">
          <PaymentProcess
            amountToPay={totalAmountToPay}
            paymentConditionId={paymentConditionId}
            paymentProcessId={paymentProcessId}
            onErrorCallback={() => {
              dispatch({
                type: 'paymentProcessError',
              });
              formik.setStatus({
                paymentProcessOnError: true,
              });
              formik.setSubmitting(false);
            }}
            paymentMethod={paymentMethod}
            shouldSkipPayment={!!selectedPaymentProvider?.shouldSkipPayment}
          />
        </div>
      ) : (
        formik.values.paymentProviderId === WECHAT_PSP_ID && <WeChatTutorial />
      )}
    </Fragment>
  );
};
